import React from 'react';
import { Box, BoxProps, styled, keyframes, css } from '@withjoy/joykit';
import { animated, useTransition } from 'react-spring';

const rootStyles: BoxProps = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  left: '0px',
  pointerEvents: 'none',
  position: 'fixed',
  top: '0px',
  zIndex: 1402,
  transform: [null, null, 'translate3d(0px, 0px, 0px)'],
};

const visibilityKeyframes = keyframes`
  to {
    visibility: visible;
  }
`;

const delayedVisibilityRules = css`
  visibility: hidden;
  animation: 0s linear 0.5s forwards ${visibilityKeyframes};
`;

const StyledRoot = styled(Box)<{ $enableDelayedVisibility?: boolean }>`
  ${props => {
    return props.$enableDelayedVisibility ? delayedVisibilityRules : {};
  }}
`;

/**
 * @internal
 * Shared props for page loader overlay components.
 */
export type PageLoaderOverlayProps = Readonly<{
  enableDelayedVisibility?: boolean;
  /** @default '#FFFFFF' */
  containerBgColor?: BoxProps['backgroundColor'];
  /** @default '100%' */
  containerHeight?: BoxProps['height'];
  /** @default '100%' */
  containerWidth?: BoxProps['width'];
  /** @default true */
  shouldAnimate?: boolean;
  /**
   * Controls whether the overlay container should be mounted.
   * If not provided, defaults to `shouldAnimate`.
   * This allows parent components to implement custom mount logic
   * (e.g., keeping overlay visible during animation wind-down).
   */
  shouldMount?: boolean;
  onAnimateStop?: () => void;
  /**
   * Delay before the leave transition starts (in ms).
   * @default 500
   */
  leaveTrail?: number;
  /** Content to render inside the overlay (spinner, logo, etc.) */
  children: React.ReactNode;
  /** Additional props to spread on the root Box element */
  rootProps?: Omit<BoxProps, 'backgroundColor' | 'width' | 'height'>;
}>;

/**
 * @internal
 * PageLoaderOverlay - Shared overlay component for full-page loaders.
 *
 * This component handles the common overlay behavior including:
 * - Fixed positioning with white background
 * - Delayed visibility animation
 * - Fade-out transition when shouldAnimate becomes false
 *
 * Used by both JoyLogoLoaderBase and GenericPageLoader.
 */
export const PageLoaderOverlay: React.FC<PageLoaderOverlayProps> = props => {
  const {
    containerBgColor = '#ffffff',
    containerHeight = '100%',
    containerWidth = '100%',
    enableDelayedVisibility,
    shouldAnimate = true,
    shouldMount,
    onAnimateStop,
    leaveTrail = 500,
    children,
    rootProps,
  } = props;

  const shouldContainerBeMounted = shouldMount ?? shouldAnimate;

  const containerTransitions = useTransition(shouldContainerBeMounted, null, {
    from: { opacity: 1 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    unique: true,
    reset: true,
    trail: shouldContainerBeMounted ? 0 : leaveTrail,
    onDestroyed: isDestroyed => {
      if (isDestroyed) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            onAnimateStop?.();
          });
        });
      }
    },
  });

  return (
    <>
      {containerTransitions.map(
        ({ item, key, props: transitionProps }) =>
          item && (
            <StyledRoot
              as={animated.div}
              key={key}
              $enableDelayedVisibility={enableDelayedVisibility}
              {...rootStyles}
              backgroundColor={containerBgColor}
              height={containerHeight}
              width={containerWidth}
              {...rootProps}
              style={transitionProps}
            >
              {children}
            </StyledRoot>
          ),
      )}
    </>
  );
};

PageLoaderOverlay.displayName = 'PageLoaderOverlay';
